import 'lib/window'

// GA4 is loaded via GTM, so `window.gtag` is not defined by default. Declaring
// the canonical gtag.js shim lets us reuse the gtag.js instance GTM already
// loaded so `gtag('get', ...)` works.
const ensureGtag = (): NonNullable<Window['gtag']> => {
  window.dataLayer = window.dataLayer || []
  if (!window.gtag) {
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer?.push(arguments)
    }
  }

  return window.gtag
}

type GaField = 'client_id' | 'session_id'

// Never block checkout: resolve to undefined if gtag does not respond in time.
// Generous enough for slow mobile, where a missed read costs the session id
// and with it the purchase's source attribution.
const GET_TIMEOUT_MS = 2000

// Fallback for client_id ONLY, when gtag('get') is slow/unavailable (common on
// slow mobile — exactly where attribution breaks). Reads `_ga` directly: its
// `GA1.x.<clientId>.<ts>` layout has been stable for years and is how sGTM's
// own GA4 client derives client_id. We deliberately do NOT parse the session
// cookie (`_ga_<streamId>`): its format changed (GS1->GS2) and is too volatile
// to reverse-engineer responsibly, so session_id comes only from gtag('get').
const readClientIdFromCookie = (): string | undefined => {
  if (typeof document === 'undefined') return undefined
  const parts = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/)?.[1].split('.')
  return parts && parts.length >= 4 ? parts.slice(-2).join('.') : undefined
}

/**
 * Reads a GA4 field (client_id / session_id) via the gtag.js shim.
 *
 * SSR-safe and resilient: resolves to `undefined` when run on the server, when
 * no measurement ID is configured, or when gtag does not respond within
 * `GET_TIMEOUT_MS`.
 */
export const getGaField = (field: GaField): Promise<string | undefined> => {
  if (typeof window === 'undefined') return Promise.resolve(undefined)

  const measurementId = process.env.GATSBY_GA4_MEASUREMENT_ID
  if (!measurementId) return Promise.resolve(undefined)

  return new Promise<string | undefined>((resolve) => {
    let settled = false
    const settle = (value: string | undefined) => {
      if (settled) return
      settled = true
      // Fall back to the `_ga` cookie for client_id only; session_id has no
      // cookie fallback (its GS2 format is too volatile to parse — see above).
      resolve(
        value || (field === 'client_id' ? readClientIdFromCookie() : undefined)
      )
    }

    const timeout = setTimeout(() => settle(undefined), GET_TIMEOUT_MS)

    try {
      const gtag = ensureGtag()
      gtag('get', measurementId, field, (value: unknown) => {
        clearTimeout(timeout)
        settle(typeof value === 'string' ? value : undefined)
      })
    } catch {
      clearTimeout(timeout)
      settle(undefined)
    }
  })
}
